import type { TerminalParamDescriptor, TerminalParamValue } from '@reform-lite/contracts';
import { useT } from '@/i18n/use-t';
import { Badge } from '@/components/ui/badge';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import type {
  ImportApplyExpectation,
  ImportPreview,
  ImportPreviewRow,
} from '@/hooks/use-terminal-params';
import { formatParamValue, valueWords } from './param-value';

/**
 * The preview an import and a copy both show before anything is written
 * (TP-FR-030): every row as will-be-added, will-change with both values,
 * unchanged, or skipped with the registry's reason. Add and change rows
 * carry a checkbox; there is no select-all, because a conflict is resolved
 * by being seen, not by a policy chosen in advance.
 */

export function rowAddress(row: Pick<ImportPreviewRow, 'key' | 'scope' | 'scopeId'>): string {
  return `${row.key}|${row.scope}|${row.scopeId}`;
}

function outcomeLabel(t: ReturnType<typeof useT>): Record<ImportPreviewRow['outcome'], string> {
  return {
    add: t('parameters.preview.willBeAdded', 'Will be added'),
    change: t('parameters.preview.willChange', 'Will change'),
    unchanged: t('parameters.preview.unchangedOutcome', 'Unchanged'),
    skip: t('parameters.preview.skippedOutcome', 'Skipped'),
  };
}

export function ImportTotals({ totals }: { totals: ImportPreview['totals'] }) {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Badge variant="success">
        {totals.add} {t('parameters.preview.toAdd', 'to add')}
      </Badge>
      <Badge>
        {totals.change} {t('parameters.preview.toChange', 'to change')}
      </Badge>
      <Badge variant="outline">
        {totals.unchanged} {t('parameters.preview.unchanged', 'unchanged')}
      </Badge>
      <Badge variant={totals.skip === 0 ? 'outline' : 'destructive'}>
        {totals.skip} {t('parameters.preview.skipped', 'skipped')}
      </Badge>
    </div>
  );
}

export function ImportPreviewTable({
  preview,
  descriptors,
  excluded,
  onToggle,
}: {
  preview: ImportPreview;
  descriptors: Map<string, TerminalParamDescriptor>;
  excluded: Set<string>;
  onToggle: (address: string, included: boolean) => void;
}) {
  const t = useT();
  const outcomes = outcomeLabel(t);
  const format = (row: ImportPreviewRow, value: TerminalParamValue | null) => {
    if (value === null) return '—';
    const descriptor = descriptors.get(row.key);
    return descriptor === undefined
      ? JSON.stringify(value)
      : formatParamValue(descriptor, value, valueWords(t));
  };
  const label = (row: ImportPreviewRow) => descriptors.get(row.key)?.label ?? row.key;
  const where = (row: ImportPreviewRow) =>
    row.scope === 'global'
      ? t('parameters.scope.global', 'global')
      : `${row.scope} ${row.scopeName ?? row.scopeId}`;

  return (
    <div className="overflow-x-auto rounded-md border bg-background">
      <Table>
        <THead>
          <TR>
            <TH className="w-10">{t('parameters.import.apply', 'Apply')}</TH>
            <TH>{t('parameters.preview.parameter', 'Parameter')}</TH>
            <TH>{t('parameters.page.scope', 'Scope')}</TH>
            <TH>{t('parameters.preview.current', 'Current')}</TH>
            <TH>{t('parameters.preview.new', 'New')}</TH>
            <TH>{t('parameters.preview.outcome', 'Outcome')}</TH>
          </TR>
        </THead>
        <TBody>
          {preview.rows.map((row) => {
            const address = rowAddress(row);
            const selectable = row.outcome === 'add' || row.outcome === 'change';
            return (
              <TR key={address} data-import-row={address}>
                <TD>
                  {selectable && (
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      aria-label={`${t('parameters.import.apply', 'Apply')} ${label(row)} ${t('parameters.common.at', 'at')} ${where(row)}`}
                      checked={!excluded.has(address)}
                      onChange={(event) => onToggle(address, event.target.checked)}
                    />
                  )}
                </TD>
                <TD>
                  <div className="text-sm">{label(row)}</div>
                  <div className="font-mono text-xs text-muted-foreground">{row.key}</div>
                </TD>
                <TD className="text-sm">{where(row)}</TD>
                <TD className="text-sm">{format(row, row.currentValue)}</TD>
                <TD className="text-sm">{format(row, row.value)}</TD>
                <TD>
                  <Badge
                    variant={
                      row.outcome === 'skip'
                        ? 'destructive'
                        : row.outcome === 'unchanged'
                          ? 'outline'
                          : row.outcome === 'add'
                            ? 'success'
                            : 'default'
                    }
                  >
                    {outcomes[row.outcome]}
                  </Badge>
                  {row.reason !== undefined && (
                    <p className="mt-1 text-xs text-muted-foreground">{row.reason}</p>
                  )}
                </TD>
              </TR>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}

/** The add and change rows the operator has not unticked. */
export function keptRows(preview: ImportPreview | null, excluded: Set<string>): ImportPreviewRow[] {
  return (preview?.rows ?? []).filter(
    (row) => (row.outcome === 'add' || row.outcome === 'change') && !excluded.has(rowAddress(row)),
  );
}

/** Bind apply to the exact incoming and current values the operator reviewed. */
export function applyExpectations(rows: readonly ImportPreviewRow[]): ImportApplyExpectation[] {
  return rows.map(({ key, scope, scopeId, value, currentValue, outcome }) => {
    if (outcome !== 'add' && outcome !== 'change') {
      throw new Error(`cannot apply a preview row with outcome ${outcome}`);
    }
    return { key, scope, scopeId, value, currentValue, outcome };
  });
}
